import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import apiClient, { tokenStorage } from "@/api/client";
import type { User } from "@/types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, role: string, age?: number, schoolId?: string, signupSource?: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  otpRequest: (target: { email?: string; phone?: string }) => Promise<void>;
  otpVerify: (target: { email?: string; phone?: string }, code: string) => Promise<void>;
  updateMe: (patch: { full_name?: string; email?: string; avatar_url?: string | null }) => Promise<User>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenStorage.get();
    if (token) {
      apiClient
        .get("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => tokenStorage.clear())
        .finally(() => setLoading(false));
    } else {
      // Silent SSO (Workstream 2 / P3): no local token, but the HttpOnly
      // foid_session cookie on .fofus.in may exist from another FOFUS site.
      // /auth/sso/session revalidates it with credentials → instant login.
      apiClient
        .get("/auth/sso/session", { withCredentials: true })
        .then(async (res) => {
          const info = res.data;
          if (info?.valid) {
            // Mint a local token via the IdP so this site's API calls are signed
            const cookieRes = await apiClient.post("/auth/sso/cookie", {}, { withCredentials: true });
            if (cookieRes.data?.token) {
              localStorage.setItem("fofus_access_token", cookieRes.data.token);
            }
            setUser({
              id: info.user_id,
              email: info.email,
              full_name: info.full_name,
              phone: null,
              avatar_url: null,
              role: info.roles?.includes("designer") ? "designer" : "customer",
              roles: info.roles || [],
              is_active: true,
              is_verified: true,
              created_at: new Date().toISOString(),
              age: null,
              school_id: null,
              school_name: null,
              school_discount_percentage: null,
            } as User);
          } else {
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
    }
  }, []);

  const fetchMe = async () => {
    const meRes = await apiClient.get("/auth/me");
    setUser(meRes.data);
  };

  /** GUEST→ACCOUNT MERGE LAW (17 Oct founder): after ANY successful login
   *  (mobile OTP / Google / password / register) the guest session's designs —
   *  including anything paid for before login — reassign to the personal
   *  account. Fire-and-forget: never blocks the post-auth UI; server-side the
   *  merge is idempotent per guest-token jti, so retries are safe.
   *  Wire shape: POST /ai/sessions/{sid}/merge {guest_token}, Authorization =
   *  the USER token; keys per api/guest.ts (fofus_guest_token / fofus_guest_session_id). */
  const mergeGuestIntoAccount = async () => {
    try {
      const sid = localStorage.getItem("fofus_guest_session_id");
      const gtok = localStorage.getItem("fofus_guest_token");
      const utok = localStorage.getItem("fofus_access_token");
      if (!sid || !gtok || !utok) return;
      await apiClient.post(
        `/ai/sessions/${sid}/merge`,
        { guest_token: gtok },
        { headers: { Authorization: `Bearer ${utok}` } },
      ).catch(() => {});
      // merged → drop the guest identity (idempotent server-side if retried)
      localStorage.removeItem("fofus_guest_session_id");
      localStorage.removeItem("fofus_guest_token");
    } catch { /* never blocks auth */ }
  };

  const login = async (email: string, password: string) => {
    const res = await apiClient.post("/auth/login", { email, password });
    tokenStorage.set(res.data.access_token);
    await fetchMe();
    void mergeGuestIntoAccount();
  };

  const register = async (email: string, password: string, fullName: string, role: string, age?: number, schoolId?: string, signupSource?: string) => {
    const payload: Record<string, unknown> = { email, password, full_name: fullName, role };
    if (age !== undefined) payload.age = age;
    if (schoolId) payload.school_id = schoolId;
    if (signupSource) payload.signup_source = signupSource;
    const res = await apiClient.post("/auth/register", payload);
    tokenStorage.set(res.data.access_token);
    await fetchMe();
    void mergeGuestIntoAccount();
  };

  const googleLogin = async (credential: string) => {
    const res = await apiClient.post("/auth/google", { credential });
    tokenStorage.set(res.data.access_token);
    await fetchMe();
    void mergeGuestIntoAccount();
  };

  const otpRequest = async (target: { email?: string; phone?: string }) => {
    await apiClient.post("/auth/otp/request", target);
  };

  const otpVerify = async (target: { email?: string; phone?: string }, code: string) => {
    const res = await apiClient.post("/auth/otp/verify", { ...target, code });
    tokenStorage.set(res.data.access_token);
    await fetchMe();
    void mergeGuestIntoAccount();
  };

  const updateMe = async (patch: { full_name?: string; email?: string }) => {
    const res = await apiClient.patch<User>("/auth/me", patch);
    setUser(res.data);
    return res.data;
  };

  const logout = () => {
    tokenStorage.clear();
    setUser(null);
    // Global logout (P3): clear the shared silent-SSO cookie on the root domain
    apiClient.post("/auth/sso/logout", {}, { withCredentials: true }).catch(() => {});
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, otpRequest, otpVerify, updateMe, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}